
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Container, Typography, Button, List, ListItem, ListItemText, ListItemSecondaryAction, IconButton, Pagination, TextField, Grid, Box, Dialog, DialogTitle, DialogContent, CircularProgress } from '@mui/material';
import { Edit, Delete, Clear } from '@mui/icons-material';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import QuestionForm from './QuestionForm';
import ConfirmationDialog from './ConfirmationDialog';
import BibleSearch from './BibleSearch';

const Questions = () => {
    const { auth } = useContext(AuthContext);
    const [questions, setQuestions] = useState([]);
    const [open, setOpen] = useState(false);
    const [editingQuestion, setEditingQuestion] = useState(null);
    const [confirmationOpen, setConfirmationOpen] = useState(false);
    const [questionToDelete, setQuestionToDelete] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalQuestions, setTotalQuestions] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
    const [bibleSearch, setBibleSearch] = useState({ book: '', chapter: '', verse: '' });
    const [resetBibleSearch, setResetBibleSearch] = useState(false);
    const [verseModalOpen, setVerseModalOpen] = useState(false);
    const [selectedVerseLinks, setSelectedVerseLinks] = useState([]);
    const [verseTexts, setVerseTexts] = useState([]);
    const [verseTextsLoading, setVerseTextsLoading] = useState(false);
    const limit = 10;

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            setPage(1);
        }, 500);

        return () => {
            clearTimeout(handler);
        };
    }, [searchTerm]);

    const fetchQuestionsAndAnswers = useCallback(async (currentPage, currentSearchTerm, currentBibleSearch) => {
        try {
            const questionsRes = await axios.get('/api/questions', {
                params: { 
                    page: currentPage, 
                    limit, 
                    q: currentSearchTerm,
                    book: currentBibleSearch.book,
                    chapter: currentBibleSearch.chapter,
                    verse: currentBibleSearch.verse
                },
                headers: { 'x-auth-token': auth.token }
            });
            
            const { questions: fetchedQuestions, total } = questionsRes.data;
            setTotalQuestions(total);
            setTotalPages(Math.ceil(total / limit));

            const questionsWithAnswers = await Promise.all(
                fetchedQuestions.map(async (question) => {
                    let answers = [];
                    let links = [];
                    if (question.question_type === 'Buttons' || question.question_type === 'TrueFalse' || question.question_type === 'TypeIn') {
                        const answersRes = await axios.get(`/api/questions/${question.id}/answers`, {
                            params: { q: currentSearchTerm },
                            headers: { 'x-auth-token': auth.token }
                        });
                        answers = answersRes.data;
                    }
                    const linksRes = await axios.get(`/api/questions/${question.id}/links`, {
                        headers: { 'x-auth-token': auth.token }
                    });
                    links = linksRes.data;
                    return { ...question, answers, links };
                })
            );
            setQuestions(questionsWithAnswers);
        } catch (err) {
            console.error(err);
        }
    }, [auth.token]);
    
    const handleOpenVerseModal = (links) => {
        setSelectedVerseLinks(links);
        setVerseModalOpen(true);
    };

    const handleCloseVerseModal = () => {
        setVerseModalOpen(false);
        setSelectedVerseLinks([]);
        setVerseTexts([]);
    };

    useEffect(() => {
        const fetchVerseTexts = async () => {
            if (verseModalOpen && selectedVerseLinks.length > 0) {
                setVerseTextsLoading(true);
                const fetchedTexts = [];
                const uniqueChapterRequests = new Map(); // To avoid duplicate API calls for the same book/chapter

                for (const link of selectedVerseLinks) {
                    const key = `${link.book_number}-${link.chapter}`;
                    if (!uniqueChapterRequests.has(key)) {
                        uniqueChapterRequests.set(key, []);
                    }
                    uniqueChapterRequests.get(key).push(link.verse);
                }

                try {
                    for (const [key, versesToExtract] of uniqueChapterRequests.entries()) {
                        const [book_number, chapter] = key.split('-');
                        const res = await axios.get(`/api/bible/books/${book_number}/${chapter}/verses`, {
                            headers: { 'x-auth-token': auth.token }
                        });
                        const chapterVerses = res.data;

                        for (const verseNumber of versesToExtract) {
                            const verseData = chapterVerses.find(v => v.verse === verseNumber);
                            if (verseData) {
                                const book = selectedVerseLinks.find(l => l.book_number === parseInt(book_number))?.book_name || `Книга ${book_number}`;
                                fetchedTexts.push({
                                    id: `${book_number}-${chapter}-${verseNumber}`,
                                    book_number: parseInt(book_number),
                                    chapter: parseInt(chapter),
                                    verse: parseInt(verseNumber),
                                    text: verseData.text,
                                    book_name: book
                                });
                            }
                        }
                    }
                    // Sort texts by book, chapter, then verse for consistent display
                    fetchedTexts.sort((a, b) => {
                        if (a.book_number !== b.book_number) return a.book_number - b.book_number;
                        if (a.chapter !== b.chapter) return a.chapter - b.chapter;
                        return a.verse - b.verse;
                    });

                    const groupedTexts = [];
                    if (fetchedTexts.length > 0) {
                        let currentGroup = [fetchedTexts[0]];

                        for (let i = 1; i < fetchedTexts.length; i++) {
                            const prev = currentGroup[currentGroup.length - 1];
                            const current = fetchedTexts[i];

                            if (
                                current.book_number === prev.book_number &&
                                current.chapter === prev.chapter &&
                                current.verse === prev.verse + 1
                            ) {
                                currentGroup.push(current);
                            } else {
                                groupedTexts.push(currentGroup);
                                currentGroup = [current];
                            }
                        }
                        groupedTexts.push(currentGroup); // Add the last group
                    }

                    const formattedGroupedTexts = groupedTexts.map(group => {
                        const firstVerse = group[0];
                        const lastVerse = group[group.length - 1];
                        const verseRange = firstVerse.verse === lastVerse.verse
                            ? `${firstVerse.verse}`
                            : `${firstVerse.verse}-${lastVerse.verse}`;
                        const fullText = group.map(v => `<strong>${v.verse}</strong>\t${v.text}`).join('\n');

                        return {
                            id: group.map(v => v.id).join('-'),
                            // The format for the bold title part: "Book Chapter:Verse(s)"
                            title: `${firstVerse.book_name} ${firstVerse.chapter}:${verseRange}`,
                            // The format for the body part
                            body: fullText
                        };
                    });

                    setVerseTexts(formattedGroupedTexts);
                } catch (err) {
                    console.error("Failed to fetch verse texts:", err);
                    setVerseTexts([{ id: 'error', text: 'Ошибка загрузки стихов.' }]);
                } finally {
                    setVerseTextsLoading(false);
                }
            }
        };

        fetchVerseTexts();
    }, [verseModalOpen, selectedVerseLinks, auth.token, questions]);

    const handlePageChange = (event, value) => {
        setPage(value);
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleBibleSearchChange = useCallback((search) => {
        setBibleSearch(search);
        setPage(1);
    }, []);

    const handleClearSearch = () => {
        setSearchTerm('');
        setDebouncedSearchTerm('');
        setBibleSearch({ book: '', chapter: '', verse: '' });
        setResetBibleSearch(true);
        setPage(1);
    };

    const handleClickOpen = () => {
        setEditingQuestion(null);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setEditingQuestion(null);
    };

    const handleEditOpen = (question) => {
        setEditingQuestion(question);
        setOpen(true);
    };

    const addQuestion = (question) => {
        fetchQuestionsAndAnswers(page, debouncedSearchTerm, bibleSearch); // Refetch after add
    };

    const editQuestion = (updatedQuestion) => {
        fetchQuestionsAndAnswers(page, debouncedSearchTerm, bibleSearch); // Refetch after edit
    };

    const handleDeleteClick = (id) => {
        setQuestionToDelete(id);
        setConfirmationOpen(true);
    };

    const handleConfirmationClose = () => {
        setConfirmationOpen(false);
        setQuestionToDelete(null);
    };

    const handleConfirmationConfirm = async () => {
        try {
            await axios.delete(`/api/questions/${questionToDelete}`, {
                headers: {
                    'x-auth-token': auth.token
                }
            });
            fetchQuestionsAndAnswers(page, debouncedSearchTerm, bibleSearch); // Refetch after delete
            handleConfirmationClose();
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        fetchQuestionsAndAnswers(page, debouncedSearchTerm, bibleSearch);
    }, [page, debouncedSearchTerm, bibleSearch, fetchQuestionsAndAnswers]);

    useEffect(() => {
        if (resetBibleSearch) {
            setResetBibleSearch(false);
        }
    }, [resetBibleSearch]);
    
    return (
        <Container>
            <Typography variant="h4" gutterBottom>
                Вопросы ({totalQuestions})
            </Typography>
            <Button variant="contained" color="primary" onClick={handleClickOpen}>
                Создать вопрос
            </Button>
            <Grid container spacing={2} alignItems="center" sx={{ mt: 2, mb: 2 }}>
                <Grid item xs={12} sm={2}>
                    <TextField
                        label="Поиск по вопросу"
                        variant="outlined"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        fullWidth
                    />
                </Grid>
                <Grid item xs={12} sm={9}>
                    <BibleSearch onSearch={handleBibleSearchChange} resetTrigger={resetBibleSearch} />
                </Grid>
                <Grid item xs={12} sm={1}>
                    <IconButton onClick={handleClearSearch} size="large">
                        <Clear />
                    </IconButton>
                </Grid>
            </Grid>
            <QuestionForm
                open={open}
                handleClose={handleClose}
                addQuestion={addQuestion}
                editQuestion={editQuestion}
                question={editingQuestion}
            />
            <ConfirmationDialog
                open={confirmationOpen}
                handleClose={handleConfirmationClose}
                handleConfirm={handleConfirmationConfirm}
                title="Удалить вопрос?"
                message="Вы уверены, что хотите удалить этот вопрос? Это действие необратимо."
            />
            <List>
                {questions.map((question) => (
                    <ListItem key={question.id} sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', borderBottom: '1px solid #e0e0e0', paddingBottom: '8px', marginBottom: '8px', position: 'relative' }}>
                        <ListItemText
                            primary={<span dangerouslySetInnerHTML={{ __html: question.question_text }}></span>}
                            secondary={question.question_type}
                            sx={{ flex: '0 0 30%', width: '30%' }} // Adjusted width
                        />
                        {(question.question_type === 'Buttons' || question.question_type === 'TrueFalse' || question.question_type === 'TypeIn') && question.answers ? (
                            <div style={{ flex: '0 0 20%', width: '20%' }}> {/* Adjusted width for Answers */}
                                <Typography component="div">
                                    <ul style={{ padding: 0, margin: 0, listStyleType: 'none' }}>
                                        {question.answers.map((a, idx) => (
                                            <li key={idx} style={{ color: a.is_correct ? 'green' : 'red' }} dangerouslySetInnerHTML={{ __html: a.answer_text }}>
                                            </li>
                                        ))}
                                    </ul>
                                </Typography>
                            </div>
                        ) : <div style={{ flex: '0 0 20%', width: '20%' }} />}
                        {question.links && question.links.length > 0 ? (
                            <div style={{ flex: '0 0 20%', width: '20%' }}> {/* Adjusted width for Bible Links */}
                                <Button onClick={() => handleOpenVerseModal(question.links)} sx={{ p: 0, textTransform: 'none', justifyContent: 'flex-start' }}>
                                    <Typography component="div" sx={{ textAlign: 'left' }}>
                                        <ul style={{ padding: 0, margin: 0, listStyleType: 'none' }}>
                                            {question.links.map((link, idx) => (
                                                <li key={idx}>
                                                    {link.book_name.substring(0, 7)} {link.chapter}:{link.verse}
                                                </li>
                                            ))}
                                        </ul>
                                    </Typography>
                                </Button>
                            </div>
                        ) : <div style={{ flex: '0 0 20%', width: '20%' }} />}
                                                    <div style={{ flex: '0 0 20%', width: '20%', paddingLeft: '8px', textAlign: 'left' }}> {/* Combined Last User and Last Update */}                            <Typography variant="body2" color="text.secondary">
                                {question.lastuser}<br/>{new Date(question.lastupdate).toLocaleDateString('en-US') + ' ' + new Date(question.lastupdate).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }).toLowerCase()}
                            </Typography>
                        </div>
                        <ListItemSecondaryAction sx={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', whiteSpace: 'nowrap' }}> {/* Fixed width for Actions */}
                            <IconButton edge="end" aria-label="edit" onClick={() => handleEditOpen(question)}>
                                <Edit />
                            </IconButton>
                            <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteClick(question.id)}>
                                <Delete />
                            </IconButton>
                        </ListItemSecondaryAction>
                    </ListItem>
                ))}
            </List>
            <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}
            />

            {/* Verse Text Modal */}
            <Dialog open={verseModalOpen} onClose={handleCloseVerseModal} fullWidth maxWidth="md">
                <DialogTitle>Тексты стихов</DialogTitle>
                <DialogContent>
                    {verseTextsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <List>
                            {verseTexts.map((vt) => (
                                <ListItem key={vt.id} sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                                    <Typography variant="subtitle1" component="div" sx={{ fontWeight: 'bold' }}>
                                        {vt.title}
                                    </Typography>
                                    <Typography variant="body1" component="div" sx={{ mt: 0.5, whiteSpace: 'pre-line' }} dangerouslySetInnerHTML={{ __html: vt.body }}>
                                    </Typography>
                                </ListItem>
                            ))}
                        </List>
                    )}
                </DialogContent>
            </Dialog>
        </Container>
    );
};

export default Questions;
